/**
 * The ground pass: what the undercarriage does to the motion the integrator has
 * already produced.
 *
 * It lives beside `step.ts` rather than inside it because contact is a
 * *constraint* on a completed motion rather than another force in the balance —
 * it runs after `advanceMotion`, reads the injected `GroundQuery`
 * (`ground.ts`) once, and hands `step` back a position, a velocity, an
 * attitude and the four edges the event collector needs.
 */

import { stanceHeightM } from "./aircraft-model-frame.ts";
import { sampleGround, type GroundQuery, type GroundSample } from "./ground.ts";
import type { FlightInputs } from "./inputs.ts";
import { bankAngle, pitchAngle, ZERO_RATES, type BodyRates, type Quat, type Vec3 } from "./math3d.ts";
import type { SimState } from "./state.ts";
import { judgeTouchdown, rollOnGround, type GearState } from "./undercarriage.ts";
import { GRAVITY_MPS2 } from "./units.ts";

/** The part of `step`s per-tick motion the ground pass reads. */
export interface GroundMotion {
  readonly position: Vec3;
  readonly velocity: Vec3;
  readonly orientation: Quat;
  readonly rates: BodyRates;
  /** Lift this tick, so the wheels carry less as the wing starts to fly. */
  readonly liftN: number;
}

export interface GroundInput {
  readonly sim: SimState;
  readonly inputs: FlightInputs;
  readonly motion: GroundMotion;
  readonly ground: GroundQuery;
  readonly dt: number;
}

export interface Grounding {
  readonly position: Vec3;
  readonly velocity: Vec3;
  readonly orientation: Quat;
  readonly rates: BodyRates;
  readonly gear: GearState;
  readonly sample: GroundSample;
  /** Wheels touching the surface this tick. */
  readonly contacted: boolean;
  /** The wheels touched down on this tick, having been in the air on the last one. */
  readonly touchedDown: boolean;
  readonly liftedOff: boolean;
  readonly collapsedNow: boolean;
  /** A safe arrival came to rest on this tick. */
  readonly landedNow: boolean;
  readonly crashed: boolean;
}

/**
 * How high the pose origin sits with the wheels (or the belly) on the surface,
 * at the attitude the aeroplane is actually in.
 *
 * **Pitch matters, and that is the point.** A taildragger pivots on its wheels,
 * so it stands lower on its skid than it does with the tail up — 0.82 m against
 * 1.41 m on a Camel — and reading one constant for both is a parked aeroplane
 * hovering or a rolling one buried (`undercarriage.ts`'s 2026-08-25 note).
 * `stanceHeightM` reads the drawn airframe's own shape at `pitchRad`.
 *
 * A folded undercarriage puts the aeroplane down on `gearCollapsedHeightFraction`
 * of that stance.
 */
function wheelHeightM(sim: SimState, surfaceM: number, collapsed: boolean, pitchRad: number): number {
  const stance = stanceHeightM(sim.config.spec.undercarriage.groundHullUnits, pitchRad);
  return surfaceM + (collapsed ? stance * sim.config.tunables.gearCollapsedHeightFraction : stance);
}

function airborneGrounding(motion: GroundMotion, gear: GearState, sample: GroundSample): Grounding {
  return {
    position: motion.position,
    velocity: motion.velocity,
    orientation: motion.orientation,
    rates: motion.rates,
    gear: { ...gear, onGround: false, stopped: false },
    sample,
    contacted: false,
    touchedDown: false,
    liftedOff: gear.onGround,
    collapsedNow: false,
    landedNow: false,
    crashed: false,
  };
}

/**
 * The ground pass. Everything the undercarriage does happens here, after the
 * forces and the integration, because contact is a *constraint* on the motion
 * that was already computed rather than another force in the balance.
 *
 * Hysteresis, in two directions and for two different reasons: the wheels are
 * down whenever the pose origin is at or below the wheel height, but the
 * aeroplane has not *left* the ground until it is `gearReleaseClearanceM` clear
 * of it — so a wing that is momentarily carrying its own weight during the roll
 * does not fire a takeoff and an immediate landing on consecutive ticks.
 */
export function applyGround(input: GroundInput): Grounding {
  const { sim, motion, ground } = input;
  const { spec, tunables } = sim.config;
  const sample = sampleGround(ground, motion.position.x, motion.position.z);
  const wheelY = wheelHeightM(sim, sample.heightM, sim.gear.collapsed, pitchAngle(motion.orientation));
  const wheelsDown = motion.position.y <= wheelY;
  if (!wheelsDown) {
    const clear = motion.position.y > wheelY + tunables.gearReleaseClearanceM;
    if (clear || !sim.gear.onGround) return airborneGrounding(motion, sim.gear, sample);
    // In the release band, still counted as rolling: no constraint applied, but
    // the gear state (and therefore the events) has not flipped yet either.
    return { ...airborneGrounding(motion, sim.gear, sample), gear: sim.gear, liftedOff: false };
  }

  const descentRateMps = -motion.velocity.y;
  const touchedDown = !sim.gear.onGround;
  const verdict = touchedDown
    ? judgeTouchdown({
        gear: spec.undercarriage,
        surface: sample.surface,
        tunables,
        descentRateMps,
        bankRad: bankAngle(motion.orientation),
        pitchRad: pitchAngle(motion.orientation),
      })
    : "safe";

  if (verdict === "crash") {
    return {
      position: { x: motion.position.x, y: wheelY, z: motion.position.z },
      velocity: { x: 0, y: 0, z: 0 },
      orientation: motion.orientation,
      rates: ZERO_RATES,
      gear: { onGround: true, collapsed: true, stopped: true },
      sample,
      contacted: true,
      touchedDown,
      liftedOff: false,
      collapsedNow: !sim.gear.collapsed,
      landedNow: false,
      crashed: true,
    };
  }

  const collapsed = sim.gear.collapsed || verdict === "gearCollapsed";
  const roll = rollOnGround({
    gear: spec.undercarriage,
    collapsed,
    surface: sample.surface,
    tunables,
    inputs: input.inputs,
    velocity: motion.velocity,
    orientation: motion.orientation,
    liftN: motion.liftN,
    weightN: spec.massKg * GRAVITY_MPS2,
    dt: input.dt,
  });
  const folded = collapsed || roll.propStruck;
  const stopped = !folded && Math.abs(roll.speedMps) <= tunables.gearRolloutStopSpeedMps;
  return {
    // The attitude the roll just settled on, not the one it arrived with: the
    // stance follows the pitch, so reading the old one would put the aeroplane
    // a tail-lift out of place for a tick.
    position: {
      x: motion.position.x,
      y: wheelHeightM(sim, sample.heightM, folded, pitchAngle(roll.orientation)),
      z: motion.position.z,
    },
    // A folded undercarriage cannot fly the aeroplane off again, so the vertical
    // velocity is held at zero rather than merely floored.
    velocity: folded ? { ...roll.velocity, y: 0 } : { ...roll.velocity, y: Math.max(motion.velocity.y, 0) },
    orientation: roll.orientation,
    rates: roll.rates,
    gear: { onGround: true, collapsed: folded, stopped },
    sample,
    contacted: true,
    touchedDown,
    liftedOff: false,
    collapsedNow: folded && !sim.gear.collapsed,
    landedNow: stopped && !sim.gear.stopped,
    crashed: false,
  };
}
